import React from 'react';
import { FuzzySearchResultDto, PartyType } from 'types/contacts';
import { logError } from 'utils/monitoring';

export type SearchType = 'NAME' | 'PERSONAL_ID' | 'REGISTRATION_NUMBER';

export interface FuzzySearchState {
    results: FuzzySearchResultDto[];
    isExpanded: boolean;
}

export interface FuzzySearchConfig {
    searchType: SearchType;
    partyTypeFilter?: PartyType;
    threshold?: number;
    minLength?: number;
    debounceMs?: number;
}

interface FuzzySearchApiParams {
    searchTerm: string;
    searchType: SearchType;
    threshold: number;
}

interface FuzzySearchTrigger {
    (args: FuzzySearchApiParams): { unwrap: () => Promise<FuzzySearchResultDto[]> };
}

export const createFuzzySearchHandler = (
    fuzzySearchParties: FuzzySearchTrigger,
    onResults: (results: FuzzySearchResultDto[]) => void,
    onExpandedChange: (expanded: boolean) => void,
    config: FuzzySearchConfig
) => {
    let debounceTimer: NodeJS.Timeout | undefined;

    const handler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const searchTerm = e.target.value;
        const { searchType, partyTypeFilter, threshold, minLength = 2, debounceMs = 500 } = config;

        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }

        debounceTimer = setTimeout(() => {
            if (searchTerm && searchTerm.trim().length > minLength) {
                const getDefaultThreshold = (searchType: string) => {
                    switch (searchType) {
                        case 'NAME':
                            return 0.45;
                        case 'PERSONAL_ID':
                        case 'REGISTRATION_NUMBER':
                            return 0.9;
                        default:
                            return 0.3;
                    }
                };

                const apiParams = {
                    searchTerm: searchTerm.trim(),
                    searchType,
                    threshold: threshold ?? getDefaultThreshold(searchType),
                };
                fuzzySearchParties(apiParams)
                    .unwrap()
                    .then((results: FuzzySearchResultDto[]) => {
                        if (results && Array.isArray(results) && results.length > 0) {
                            const filteredResults = partyTypeFilter
                                ? results.filter((result) => result.partyType === partyTypeFilter)
                                : results;
                            onResults(filteredResults);
                        } else {
                            onResults([]);
                        }
                    })
                    .catch((error: unknown) => {
                        console.error('Fuzzy search error:', error);
                        logError(error);
                        onResults([]);
                        onExpandedChange(false);
                    });
            } else {
                onResults([]);
                onExpandedChange(false);
            }
        }, debounceMs);
    };

    const cleanup = () => {
        if (debounceTimer) {
            clearTimeout(debounceTimer);
        }
    };

    return { handler, cleanup };
};
